export const capabilityValues = [
  'player.create',
  'player.update',
  'player.delete',
  'player.availability.update',
  'player.compare',
  'report.create.performance',
  'report.create.scouting',
  'report.create.medical',
  'report.create.training',
  'report.update',
  'report.delete',
  'report.revision.create',
  'report.attachment.create',
  'report.attachment.delete',
  'report.export',
  'player.document.upload.medical',
  'player.document.upload.scouting',
  'player.document.delete',
  'lineup.manage',
  'fixture.create',
  'fixture.update',
  'fixture.delete',
  'fixture.squad.update',
  'fixture.review.update',
  'fixture.note.create',
  'fixture.attachment.create',
  'fixture.attachment.delete',
  'fixture.export',
  'wyscout.sync',
  'wyscout.analysis.view',
  'wyscout.export',
  'wyscout.squad.sync',
  'calendar.event.create',
  'calendar.event.update',
  'calendar.event.delete',
  'calendar.assignment.manage',
  'calendar.group.manage',
  'calendar.attachment.upload',
  'calendar.attachment.delete',
  'season.view',
  'season.export',
  'season.standings.update',
  'season.standings.sync',
  'league.read',
  'league.export',
  'league.compare',
  'league.sync',
  'league.analysis.view',
  'scouting.watchlist.manage',
] as const;

export type Capability = typeof capabilityValues[number];

export interface V2BrowseResponse<T, F> {
  data: T[];
  meta: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
  facets: F;
  capabilities: Capability[];
}

export interface V2DetailResponse<T> {
  data: T;
  capabilities: Capability[];
}

export interface V2ApiError {
  code: string;
  message: string;
  details: Record<string, unknown>;
  requestId: string;
}

interface V2ErrorEnvelope {
  response?: {
    data?: {
      error?: {
        code?: string;
        message?: string;
        details?: unknown;
        request_id?: string;
      };
    };
  };
}

export const hasCapability = (
  capabilities: readonly Capability[],
  expected: Capability,
): boolean => capabilities.includes(expected);

export const getV2Error = (error: unknown): V2ApiError => {
  const payload = (error as V2ErrorEnvelope)?.response?.data?.error;
  const structured = typeof payload?.code === 'string' && typeof payload.message === 'string';
  if (structured) {
    const details = typeof payload.details === 'object'
      && payload.details !== null
      && !Array.isArray(payload.details)
      ? payload.details as Record<string, unknown>
      : {};
    return {
      code: payload.code as string,
      message: payload.message as string,
      details,
      requestId: typeof payload.request_id === 'string' ? payload.request_id : '',
    };
  }
  if (
    typeof (error as Partial<V2ApiError>)?.code === 'string'
    && typeof (error as Partial<V2ApiError>)?.message === 'string'
  ) {
    const direct = error as Partial<V2ApiError>;
    return {
      code: direct.code as string,
      message: direct.message as string,
      details: direct.details && typeof direct.details === 'object' ? direct.details : {},
      requestId: typeof direct.requestId === 'string' ? direct.requestId : '',
    };
  }

  return {
    code: 'unexpected_error',
    message: 'Something went wrong. Please try again.',
    details: {},
    requestId: '',
  };
};
